import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
import { teamLoginFor } from './login.js';
import { teamLoginSchema, teamSchema } from './team.js';
import { teamCoverageSchema } from './team-coverage.js';
import { teamLoadSchema } from './team-load.js';
import { issuedCredentialsSchema, publicUserSchema } from './user.js';
/**
 * Composer une équipe (M12, M22) : qui y est, et ce qu'elle a sur les bras. C'est le contrat de
 * l'écran « Équipes » du back-office, et le seul endroit où la règle des deux agents est écrite.
 *
 * ⚠️ **Ce module traverse deux dossiers du domaine** — `auth/` pour l'équipe et ses membres,
 * `inquiry/` pour la charge — parce que l'écran, lui, les montre ensemble. Le garder en feuille
 * évite d'accrocher les statuts de M3 à `team.ts`, qui n'en a que faire.
 */
/**
 * Les deux agents d'une équipe (M12) — plafond de **service**, vérifié par un comptage à
 * l'ajout : rien en base n'empêche un troisième, et c'est délibéré.
 *
 * ⚠️ **Il compte les agents, pas les lignes de `Membership`.** Le compte partagé de la tablette
 * (`role = 'team'`) passe par la même table (M22) sans occuper une des deux places : une équipe
 * complète, tablette comprise, porte donc **trois** lignes. Confondre les deux interdirait de
 * rattacher son compte à une équipe déjà pourvue de ses deux agents — le cas normal.
 */
export const MAX_TEAM_AGENTS = 2;
/**
 * Les seuls rôles que M12 laisse entrer dans `Membership` : l'agent nominatif, et le compte
 * partagé de la tablette.
 *
 * ⚠️ **Écrite ici, et nulle part ailleurs.** Trois appelants en dépendent — le service qui refuse
 * les autres, le hook qui filtre les comptes affectables, le sélecteur qui ne propose que ce qui
 * aboutira. Réénumérée sur place, elle diverge en trois points le jour où elle bouge, et rien ne
 * le signale.
 *
 * ⚠️ Un 6ᵉ rôle ajouté à `ROLES` **n'entre pas ici de lui-même**, contrairement à
 * `NOMINATIVE_ROLES` du back-office : appartenir à une équipe est une décision de M12, pas un
 * défaut. Le `satisfies` garantit seulement que ces valeurs sont des rôles réels ; c'est
 * `partitionTeamMembers` que le spec confronte à cette liste, pour qu'un rôle admis ici mais
 * oublié là ne finisse pas silencieusement en `unexpected`.
 */
export const TEAM_MEMBER_ROLES = ['agent', 'team'];
/** Ce compte peut-il appartenir à une équipe (M12) ? Le prédicat que les trois appelants lisent. */
export function isTeamMemberRole(role) {
    return TEAM_MEMBER_ROLES.includes(role);
}
/**
 * Ce que « charge de contrôles » veut dire, statut par statut (M3) — l'équipe les a devant elle.
 *
 * Trois listes plutôt que deux : nommer ce qui n'est compté **nulle part** est ce qui rend le
 * partage vérifiable, et `team-composition.spec.ts` refuse qu'un statut y manque ou y figure deux
 * fois. Sans ce filet, un huitième statut ajouté à `INQUIRY_STATUSES` sortirait des compteurs en
 * silence, et une équipe afficherait une charge fausse sans que rien ne le signale.
 */
export const TEAM_LOAD_PENDING_STATUSES = [
    'assigned',
    'in_progress',
];
/** Transmis et au-delà : l'équipe a fait son travail, la suite appartient au back-office. */
export const TEAM_LOAD_DONE_STATUSES = [
    'submitted',
    'validated',
    'archived',
];
/**
 * Ni à faire, ni fait — et ce n'est pas un oubli. `received` porte parfois encore l'équipe d'une
 * affectation défaite (M4 : `assigned → received`), `dropped` est un dossier abandonné : les
 * compter dans « à faire » enverrait une équipe sur des contrôles que plus personne n'attend.
 */
export const TEAM_LOAD_UNCOUNTED_STATUSES = [
    'received',
    'dropped',
];
/**
 * Ce que recouvre une valeur de `load`, statut par statut — la **seule** traduction entre le
 * filtre du tableau et les listes ci-dessus.
 *
 * ⚠️ **`'all'` n'entre pas, et le type l'interdit.** « Tous » n'est pas un ensemble à énumérer
 * mais l'absence de condition : rendre les sept statuts obligerait l'appelant à distinguer une
 * liste complète d'un `WHERE` omis, et ferait évaluer à Postgres un `IN` toujours vrai, ligne à
 * ligne. Le `Exclude` fait de cette distinction une erreur de compilation plutôt qu'une lenteur.
 *
 * ⚠️ **Écrite ici parce que les listes y sont.** L'écran qui affiche les deux onglets ne l'appelle
 * pas : il passe `'pending'` ou `'done'` et n'énumère aucun statut. C'est le dépôt côté serveur
 * qui traduit, une fois.
 */
export function statusesForLoad(load) {
    return load === 'pending' ? TEAM_LOAD_PENDING_STATUSES : TEAM_LOAD_DONE_STATUSES;
}
export { teamLoadSchema } from './team-load.js';
/**
 * Les « **contrôles attribués** » du prototype : le grand nombre de la carte d'équipe. C'est le
 * **total** des deux compteurs, pas l'un des deux — une équipe qui a tout transmis affiche bien
 * son volume de tournée, elle ne retombe pas à zéro.
 */
export function assignedControls(load) {
    return load.pending + load.done;
}
/**
 * L'« **avancement** » du prototype, entre 0 et 1 : la part de la tournée déjà transmise.
 *
 * ⚠️ **Une équipe sans aucun contrôle est à 0, pas à 1.** La division serait `0/0` : le repli est
 * un choix, et « rien à faire donc tout est fait » afficherait une barre pleine sur une équipe qui
 * n'a rien reçu — le contraire de ce que la carte sert à voir d'un coup d'œil.
 *
 * Écrit ici et pas dans l'écran : c'est la **définition** d'un indicateur métier, et le jour où
 * un tableau de bord l'affichera aussi, les deux devront s'accorder.
 */
export function teamProgress(load) {
    const assigned = assignedControls(load);
    return assigned === 0 ? 0 : load.done / assigned;
}
/**
 * Une équipe telle que l'écran « Équipes » la lit : la ligne `Team`, ses membres, sa charge et sa
 * couverture.
 *
 * ⚠️ **`members` est plat, et il le reste.** Séparer les agents du compte d'équipe dans le
 * contrat aurait fait disparaître de l'écran une ligne de `Membership` portant un rôle inattendu
 * — or c'est une anomalie d'administration, et la cacher la rend introuvable (M21). Le partage se
 * fait à l'affichage, par `partitionTeamMembers`, qui rend ces lignes-là au lieu de les avaler.
 *
 * `coverage` porte les communes de l'équipe et, par elles, son marché (`teamMarket`, M22).
 */
export const teamCompositionSchema = teamSchema.extend({
    members: z.array(publicUserSchema),
    load: teamLoadSchema,
    coverage: teamCoverageSchema,
});
/**
 * Ce que rend la création d'un **groupe** : l'équipe composée, et les accréditations de sa
 * tablette.
 *
 * ⚠️ **Un seul geste, un seul appel** — c'est ce que dit le prototype validé, où « Créer un
 * groupe » (« Comptes équipes ») et « Créer une équipe » (« Équipes ») mènent à la **même** page
 * (`data-nav="team-create"`), et où une équipe *porte* son login et son mot de passe. Notre modèle
 * les sépare (M22 : `Team`, `User(role='team')`, `Membership`), mais le geste ne se sépare pas :
 * une équipe sans compte n'a pas de tablette, un compte sans équipe n'a pas de tournée.
 *
 * D'où quatre écritures en **une** transaction côté serveur — la `Team`, les deux `Membership`
 * d'agents, le `User` partagé et son `Membership`. Les enchaîner depuis le navigateur laissait
 * une équipe à moitié composée derrière le premier échec réseau.
 *
 * ⚠️ **`credentials` porte le mot de passe en clair, et c'est la seule occasion de le lire** (M6) :
 * seul son hachage est stocké. À l'écran de le montrer, à personne de le journaliser.
 */
export const createdTeamSchema = z.object({
    team: teamCompositionSchema,
    credentials: issuedCredentialsSchema,
    /**
     * Ce que la création a laissé passer **en le signalant** — M21 : « on enregistre, on signale,
     * l'humain tranche ». Un seul motif à ce jour : la paire d'agents qui compose déjà une équipe.
     *
     * ⚠️ **Le signalement ne peut pas s'arrêter au journal du serveur.** L'écran `/teams/new`
     * prévient avant le clic parce qu'il a lu `GET /teams/composition` ; un client d'API, lui,
     * recevait un `201` sans un mot et créait le doublon à l'aveugle (#176).
     *
     * ⚠️ **Des phrases, pas des codes.** Un code demanderait un terme au glossaire et un libellé
     * chez chaque appelant, pour une liste qui tient sur une ligne. Le jour où un appelant devra
     * *réagir* à un motif au lieu de l'afficher, ce sera le moment de le nommer.
     *
     * ⚠️ **Toujours là, vide quand il n'y a rien** : personne n'a à distinguer « champ absent » de
     * « rien à signaler ». Le défaut sert la relecture d'une réponse ancienne, pas l'écriture.
     */
    warnings: z.array(z.string()).default([]),
});
/**
 * Ce que l'appelant fournit pour affecter quelqu'un à une équipe. L'équipe vient du chemin, le
 * rôle de la ligne `User` : rien d'autre à fournir, `Membership` n'ayant aucune autre colonne (M22).
 *
 * ⚠️ **`strictObject`, pas `object`** : un `role` ou un `teamId` de trop se rejette au lieu de se
 * retirer en silence — l'appelant croirait l'avoir imposé.
 */
export const addTeamMemberSchema = z.strictObject({
    userId: uuidV7Schema,
});
/**
 * Ce que l'appelant fournit pour donner **sa tablette** à une équipe qui n'en a pas (M22).
 *
 * ⚠️ **Cette route existe pour réparer, pas pour composer.** Depuis la création transactionnelle
 * de `POST /teams` (04/09/2026), une équipe naît avec son compte partagé : celles qui n'en ont
 * pas sont **antérieures** à cette création — constaté sur `api-dev` le 11/09/2026, où elles
 * n'affichaient ni identifiant, ni mot de passe, ni aucun geste. Sans ce geste, elles ne peuvent
 * ni travailler ni se réparer depuis l'interface.
 *
 * ⚠️ **`login` est facultatif, et son absence n'est pas un oubli** : le serveur le **propose**
 * alors à partir des initiales des agents de l'équipe (`buildTeamLogin`, M6). L'écran pré-remplit
 * cette même proposition et laisse l'humain la remplacer — parce que ces équipes-là ont souvent
 * un nom hors format (« TA-GL », « wass ») dont aucune initiale plausible ne se tire.
 *
 * ⚠️ **`strictObject`, pas `object`** : un `password` de trop se rejette au lieu de se retirer en
 * silence. Il se tire côté serveur, comme partout ailleurs (M6).
 */
export const createTeamAccountSchema = z.strictObject({
    login: teamLoginSchema.optional(),
});
/**
 * Range les membres d'une équipe selon M12. Écrit **une fois ici** parce que deux appelants en
 * dépendent — le service qui compte les places libres, l'écran qui dessine les cartes — et qu'une
 * seconde lecture de la règle finirait par diverger de celle-ci.
 *
 * Le **premier** compte d'équipe rencontré gagne, les suivants passent en `unexpected` : il faut
 * bien en désigner un, et l'ordre reçu est le seul critère stable dont on dispose.
 */
export function partitionTeamMembers(members) {
    const partitioned = { agents: [], account: null, unexpected: [] };
    for (const member of members) {
        if (member.role === 'agent')
            partitioned.agents.push(member);
        else if (member.role === 'team' && partitioned.account === null)
            partitioned.account = member;
        else
            partitioned.unexpected.push(member);
    }
    return partitioned;
}
/**
 * Les places d'agent encore libres. Jamais négatif : une équipe qui en porte déjà trois — la base
 * l'autorise — annonce zéro place, pas une place en trop.
 */
export function freeAgentSlots(members) {
    return Math.max(0, MAX_TEAM_AGENTS - partitionTeamMembers(members).agents.length);
}
/**
 * Le point médian qui sépare les initiales dans `Team.name` (M22) — `JL·TB`.
 *
 * ⚠️ **U+00B7 (point médian), pas un point ni une puce.** C'est le caractère du prototype validé,
 * et il voyage dans une valeur de base : le remplacer par un `.` casserait la relecture d'un nom
 * existant, par un `•` la comparaison avec un nom déjà écrit.
 */
export const TEAM_NAME_SEPARATOR = '·';
/**
 * Les initiales d'une personne — prénom puis nom, en capitales : `Jeanne Leroy` donne `JL`.
 *
 * C'est l'entrée de deux dérivations de M6/M22 : le **nom** de l'équipe (`buildTeamName`) et le
 * **login** de son compte partagé (`buildTeamLogin`, qui reçoit les deux paires). Les écrans s'en
 * servent aussi pour les pastilles d'avatar — la même règle, donc le même code.
 *
 * ⚠️ **Repli sur le `login`** — `d.jean` donne `DJ` — plutôt que sur une chaîne vide : `login` est
 * le seul champ qu'un compte porte toujours (M6), l'état civil étant nullable. Sans ce repli, une
 * pastille muette ne distinguerait plus deux comptes sans état civil, et une équipe fabriquée à
 * partir d'eux s'appellerait `·`.
 */
export function personInitials(account) {
    const fromName = [account.firstName, account.lastName]
        .map((part) => (part ?? '').trim().charAt(0))
        .join('');
    const source = fromName === '' ? account.login.split('.').map(firstCharacter).join('') : fromName;
    return source.toUpperCase();
}
const firstCharacter = (part) => part.charAt(0);
/**
 * Les initiales des agents d'une équipe, séparées comme dans son nom par défaut : `JL·TB`. C'est
 * ce que le rapport imprime sous « Contrôleur » (#417) — et non `Team.name`, qu'on renomme
 * librement (M22) et qui peut ne plus rien dire de qui a contrôlé.
 *
 * `null` pour une équipe sans agent : une chaîne vide s'imprimerait comme une valeur.
 */
export function teamInitials(agents) {
    if (agents.length === 0)
        return null;
    return agents.map(personInitials).join(TEAM_NAME_SEPARATOR);
}
/**
 * Le nom d'une équipe, dérivé de ses deux agents (M22) : `JL·TB`. C'est ce que fait la page
 * « Créer une équipe » du prototype validé — on coche deux agents, le nom s'écrit tout seul.
 *
 * ⚠️ **Elle propose, elle ne décide pas** (11/09/2026). M22 tient `Team.name` pour un champ libre :
 * une équipe se renomme, et son nom peut ne plus rien devoir à ses initiales. Rappelée pour
 * « rafraîchir » le nom d'une équipe existante **côté serveur**, elle écraserait en silence un nom
 * que quelqu'un a choisi — c'est le garde-fou n°3. Un écran, lui, a le droit de l'afficher comme
 * **défaut** quand le binôme change : `proposeTeamIdentity` est ce chemin-là, et il passe par un
 * humain qui valide.
 */
export function buildTeamName(first, second) {
    return `${personInitials(first)}${TEAM_NAME_SEPARATOR}${personInitials(second)}`;
}
export function proposeTeamIdentity(members) {
    const { agents } = partitionTeamMembers(members);
    const [first, second] = agents;
    if (agents.length !== MAX_TEAM_AGENTS || first === undefined || second === undefined)
        return null;
    const login = teamLoginFor(personInitials(first), personInitials(second));
    return login === null ? null : { name: buildTeamName(first, second), login };
}
//# sourceMappingURL=team-composition.js.map